// THE HANDOFF — the content travels the way the directory did.
//
// When the place changes, the page you were looking at does not simply vanish
// under the new one. A copy of it (its DOM, cloned the instant before React
// replaces it) stays exactly where it was for a breath and drifts away toward
// the side you left it by, while the new place arrives from the side you are
// heading. One view at a time: the old one is mostly gone (ease-out, 120ms)
// before the new one, which waits ~70ms, is mostly there — so the two never
// stand at half opacity together (the deck's "flash", a double exposure).
//
// The copy is a still: every animation in it is held at its end and nothing in
// it transitions (a fresh node would replay its entrance from frame one and
// fade IN while it fades out), it is inert and hidden from assistive tech, and
// live media is swapped for an empty box of the same size (a cloned iframe
// would load its page again; a cloned canvas has no pixels anyway). It keeps
// its ids: the live view comes first in the document, so `url(#…)` paints and
// lookups still find the live nodes, and the copy's gradients still resolve.
// It is SCROLLED to where the old view was rather than moved there, so anything
// sticky in it rests where it rested, and inner strips keep their offsets.

export interface Ghost {
  node: HTMLElement;
  /** the scroller's offset when the copy was taken (0 for a head that doesn't scroll). */
  top: number;
  /** the geometry the copy stood in (the navigator's height, the fold), held still */
  vars: [string, string][];
  /** inner scroll offsets: [index among the copy's elements, left, top] */
  offsets: [number, number, number][];
  /** the box it stood in, held still: the next view may change the stage's
   *  variant (into or out of the reader), and the copy must not restyle with it */
  pad: string | null;
  mask: string | null;
}

/** above this many elements the copy costs more than it gives: the old view simply goes. */
const MAX_NODES = 3500;
export const GHOST_OUT_MS = 120;
export const ENTER_DELAY_MS = 70;

const FROZEN_VARS = ["--stage-head-h", "--lock-h", "--lock-gap", "--lock-fold"];
const MEDIA = "iframe, video, audio, embed, object, canvas";
const LIVE_ATTRS = ["data-hl-target", "data-hint", "data-hint-key", "data-node-path", "data-nav-key", "data-look"];

export function captureGhost(col: HTMLElement, scroller: HTMLElement | null, root: HTMLElement | null): Ghost | null {
  if (typeof window === "undefined") return null;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return null;
  const all = col.getElementsByTagName("*");
  if (all.length > MAX_NODES) return null;

  // inner offsets, read while the old layout is still clean
  const offsets: [number, number, number][] = [];
  for (let i = 0; i < all.length; i++) {
    const e = all[i] as HTMLElement;
    if (e.scrollLeft || e.scrollTop) offsets.push([i, e.scrollLeft, e.scrollTop]);
  }
  // the geometry it stood in: the next view may bring a taller or shorter navigator
  const vars: [string, string][] = [];
  const pad = getComputedStyle(col).paddingTop || null;
  let mask: string | null = null;
  if (scroller) {
    const m = getComputedStyle(scroller);
    mask = m.maskImage || m.getPropertyValue("-webkit-mask-image") || null;
    if (mask === "none") mask = null;
  }
  if (root) {
    const cs = getComputedStyle(root);
    for (const v of FROZEN_VARS) {
      const val = cs.getPropertyValue(v).trim();
      if (val) vars.push([v, val]);
    }
    if (scroller) {
      const edge = getComputedStyle(scroller).getPropertyValue("--edge-fold").trim();
      if (edge) vars.push(["--edge-fold", edge]);
    }
  }

  const node = col.cloneNode(true) as HTMLElement;
  // live media → an empty box of the same size, in the same place
  const live = col.querySelectorAll<HTMLElement>(MEDIA);
  const copies = node.querySelectorAll<HTMLElement>(MEDIA);
  copies.forEach((c, i) => {
    const o = live[i];
    const box = document.createElement("div");
    box.style.width = `${o?.offsetWidth ?? 0}px`;
    box.style.height = `${o?.offsetHeight ?? 0}px`;
    c.replaceWith(box);
  });
  for (const a of LIVE_ATTRS) node.querySelectorAll(`[${a}]`).forEach((e) => e.removeAttribute(a));
  node.removeAttribute("data-handoff");
  node.setAttribute("inert", "");
  node.setAttribute("aria-hidden", "true");
  return { node, top: scroller?.scrollTop ?? 0, vars, offsets, pad, mask };
}

/**
 * Play the copy out of `layer`, drifting toward −dir (the side we left by).
 * Resolves when the copy has actually begun to leave (its first frame is on
 * screen) — a large copy takes a frame or two to paint, and the new view must
 * wait for THAT, not for the clock, or the two stand half-lit together.
 */
export function playGhost(g: Ghost, layer: HTMLElement, dir: 1 | -1): Promise<void> {
  layer.getAnimations({ subtree: true }).forEach((a) => a.cancel());
  layer.replaceChildren();
  for (const [k, v] of g.vars) layer.style.setProperty(k, v);
  if (g.pad) g.node.style.paddingTop = g.pad;
  if (g.mask) {
    layer.style.setProperty("mask-image", g.mask);
    layer.style.setProperty("-webkit-mask-image", g.mask);
  }
  const frame = document.createElement("div");
  frame.className = "stage-ghost__frame";
  frame.appendChild(g.node);
  layer.appendChild(frame);
  // stand where it stood: scrolled, not moved (sticky and inner strips stay true)
  if (g.top) layer.scrollTop = g.top;
  if (g.offsets.length) {
    const els = g.node.getElementsByTagName("*");
    for (const [i, l, t] of g.offsets) {
      const e = els[i] as HTMLElement | undefined;
      if (e) { e.scrollLeft = l; e.scrollTop = t; }
    }
  }
  const done = () => {
    frame.remove();
    if (!layer.firstChild) {
      for (const [k] of g.vars) layer.style.removeProperty(k);
      layer.style.removeProperty("mask-image");
      layer.style.removeProperty("-webkit-mask-image");
    }
  };
  try {
    const a = frame.animate(
      [
        { opacity: 1, transform: "none" },
        { opacity: 0, transform: `translate3d(${-dir * 10}px, 0, 0)` },
      ],
      { duration: GHOST_OUT_MS, easing: "cubic-bezier(0.22, 0.61, 0.36, 1)", fill: "forwards" },
    );
    a.onfinish = done;
    a.oncancel = done;
    return a.ready.then(() => undefined, () => undefined);
  } catch {
    done();
    return Promise.resolve();
  }
}

/**
 * Hold the arriving view (`box`, carrying the entrance animation through its
 * child) until the outgoing copy has begun to leave, then let it in a beat
 * later. `data-handoff="hold"` pauses the entrance at its first frame (fully
 * transparent, `both` fill); "go" plays it. Never longer than `cap`.
 */
export function sequenceArrival(box: HTMLElement, leaving: Promise<void>, cap = 260): void {
  // a token per hold: a quick second move re-holds the same box, and the first
  // move's timers must not let the second view in early
  const token = String((Number(box.dataset.handoffSeq) || 0) + 1);
  box.dataset.handoffSeq = token;
  box.setAttribute("data-handoff", "hold");
  let gone = false;
  const go = () => {
    if (gone || box.dataset.handoffSeq !== token) return;
    gone = true;
    window.setTimeout(() => {
      if (box.dataset.handoffSeq === token) box.setAttribute("data-handoff", "go");
    }, ENTER_DELAY_MS - 20);
  };
  void leaving.then(go);
  window.setTimeout(go, cap);
}
